import "./App.css";
import React from "react";
import ReactGA from "react-ga";
import Intro from "./components/intro/intro";
import Themes from "./components/themes/themes";
import Search from "./components/search/search";
import Explore from "./components/explore/explore";
import useScienceData from "./hooks/use-science-data";
import { useIsMobile } from "./hooks/mobile.js";

ReactGA.initialize("UA-11963560-5");

const DEFAULT_VIEW = "bubbles";
const SUBTHEME_INITIAL_STATE = {
    "How can we optimise our management impact?": [],
    "How can we improve our management through innovation and technology?": [],
    "What is the condition and trend of key values?": [],
    "How is the Reef used?": [],
};

function App() {
    const { opportunitiesData, themesData, childrenData, topicsData } = useScienceData();
    const [themes, setThemes] = React.useState(null);
    const [opportunities, setOpportunities] = React.useState(null);
    const [topics, setTopics] = React.useState(null);
    const [searchOptions, setSearchOptions] = React.useState(null);
    const [filteredOpportunities, setFilteredOpportunities] = React.useState(null);
    const [filteredTopics, setFilteredTopics] = React.useState(null);
    const [topicFilter, setTopicFilter] = React.useState([]);
    const [themeFilter, setThemeFilter] = React.useState([]);
    const [subThemeFilter, setSubThemeFilter] = React.useState(SUBTHEME_INITIAL_STATE);
    const [keywordFilter, setKeywordFilter] = React.useState([]);
    const [opportunitySearch, setOpportunitySearch] = React.useState(null);
    const [searchFilter, setSearchFilterState] = React.useState([]);
    const [searchState, setSearchState] = React.useState(null);
    const [selectedOpportunity, setSelectedOpportunity] = React.useState(null);
    const [view, setView] = React.useState(DEFAULT_VIEW);
    const [hideCompleted, setHideCompleted] = React.useState(true);
    const [completed, setCompleted] = React.useState(0);
    const [ga, setGA] = React.useState(null);

    const isMobile = useIsMobile();

    React.useEffect(() => {
        if (opportunities && topics) {
            const opps = hideCompleted ? opportunities.filter(d => d.status !== "Completed") : opportunities;
            setCompleted(opportunities.filter(d => d.status === "Completed").length);
            setFilteredOpportunities(opps);
            setFilteredTopics(topics);
        }
    }, [opportunities, topics]);

    const handleTopicFilter = filters => {
        handleFilteredOpportunities(filters, "topic");
        if (filters.length > 0) {
            ReactGA.event({
                category: "SCIKNOW: filter",
                action: "topic",
                label: filters.map(d => d.label).join(", "),
            });
        } else {
            ReactGA.event({
                category: "SCIKNOW: filter",
                action: "topic",
                label: "reset",
            });
        }
    };

    const handleKeywordFilter = filters => {
        handleFilteredOpportunities(filters, "keyword");
        if (filters.length > 0) {
            ReactGA.event({
                category: "SCIKNOW: filter",
                action: "keyword",
                label: filters.map(d => d.label).join(", "),
            });
        } else {
            ReactGA.event({
                category: "SCIKNOW: filter",
                action: "keyword",
                label: "reset",
            });
        }
    };

    const handleThemeFilter = filters => {
        let newFilters = filters;

        // this might be coming from the checkbox
        if (newFilters.target) {
            let themesFilterValues = themeFilter;

            const target = filters.target;
            const ticked = target.type === "checkbox" ? target.checked : target.value;
            const slug = target.name;

            // if false, take it out of the list of newFilters.
            // if true, add it to the list of newFilters
            if (!ticked) {
                newFilters = themesFilterValues.filter(d => d.value !== slug);
            } else {
                newFilters = [
                    ...themesFilterValues,
                    {
                        label: themes.find(d => d.slug === slug).theme,
                        value: slug,
                    },
                ];
            }
        }

        handleFilteredOpportunities(newFilters, "theme");

        ReactGA.event({
            category: "SCIKNOW: filter",
            action: "theme",
            label: newFilters.map(d => d.label).join(", "),
        });
    };

    const handleSubThemeFilter = (filters, theme) => {
        handleFilteredOpportunities(filters, "sub-theme", theme);

        ReactGA.event({
            category: "SCIKNOW: filter",
            action: "sub-theme",
            label: theme + ": " + filters.map(d => d.label).join(", "),
        });
    };

    const handleToggleView = view => {
        setView(view);
        ReactGA.event({
            category: "SCIKNOW: mobile",
            action: "toggle",
            label: view,
        });
    };

    const handleHideCompleted = () => {
        const c = !hideCompleted;
        setHideCompleted(c);

        handleFilteredOpportunities(c, "completed");
    };

    const handleScrollToTop = () => {
        const element = document.querySelector("#science-knowledge-needs-explore");
        window.scrollTo({ top: element.offsetTop - 30, behaviour: "smooth" });

        ReactGA.event({
            category: "SCIKNOW: explore",
            action: "back to top",
        });
    };

    // generic handle
    const handleFilterAndSearch = filter => {
        if (filter) {
            if (filter.type === "opportunity") {
                // reset otherwise it won't be in the list
                handleReset();
                setSelectedOpportunity(filter.label);
                if (isMobile) handleToggleView("list");
                ReactGA.event({
                    category: "SCIKNOW: search opportunities",
                    action: "opportunity",
                    label: filter.filter,
                });
            } else {
                handleFilteredOpportunities([filter], filter.type);
                const element = document.querySelector("#science-knowledge-needs-search");
                window.scrollTo({ top: element.offsetTop - 30, behaviour: "smooth" });

                ReactGA.event({
                    category: "SCIKNOW: search opportunities",
                    action: filter.type,
                    label: filter.label,
                });
            }
            setSearchFilterState(filter);
        }
    };

    const handleOpportunityInput = input => {
        setOpportunitySearch(input.target.value);

        if (input.target.value.length === 0) {
            setFilteredTopics(topics);
            setFilteredOpportunities(opportunities);

            ReactGA.event({
                category: "SCIKNOW: search",
                action: "clear",
            });
        }
    };

    const handleOpportunitySearchReset = () => {
        setOpportunitySearch([]);

        setFilteredTopics(topics);
        setFilteredOpportunities(opportunities);

        ReactGA.event({
            category: "SCIKNOW: search",
            action: "reset",
        });
    };

    const handleOpportunitySearch = () => {
        handleFilteredOpportunities("", "search");
        ReactGA.event({
            category: "SCIKNOW: search",
            action: "enter",
            label: opportunitySearch,
        });
    };

    const handleReset = () => {
        ReactGA.event({
            category: "SCIKNOW: all",
            action: "reset",
        });
        setTopicFilter([]);
        setThemeFilter([]);
        setSubThemeFilter(SUBTHEME_INITIAL_STATE);
        setKeywordFilter([]);
        setSearchFilterState([]);
        setSearchState(null);

        // on reset, we don't show completed
        const opps = opportunities.filter(d => d.status !== "Completed");
        setFilteredOpportunities(opps);

        setFilteredTopics(topics);
        setSelectedOpportunity(null);
        setOpportunitySearch([]);
        setHideCompleted(true);

        setCompleted(opportunities.filter(d => d.status === "Completed").length);
    };

    // get the current filters, add this one.
    // do an OR for each filter type
    // available filters are: themes (and sub-themes) OR topics OR keyword OR search
    const handleFilteredOpportunities = (filters, filterType, themeForSubThemeFilter = null) => {
        // the filterType needs to be read and set.
        // the others (if they are not empty) need to be read.

        let topicsFilterValues = topicFilter;
        let themesFilterValues = themeFilter;
        let subThemesFilterValues = subThemeFilter;
        let keywordsFilterValues = keywordFilter;
        let hideCompletedFilter = hideCompleted;

        // the state requires the value and label but for filtering, we only require one or the other.
        // so setX always uses label and value
        // while xFilterValues is just a list of labels or values

        switch (filterType) {
            case "topic":
                topicsFilterValues = filters;
                setTopicFilter(filters);
                break;
            case "theme":
                themesFilterValues = filters;
                setThemeFilter(filters);

                // if the theme has been turned off, sub-themes need to go off too
                let stt = SUBTHEME_INITIAL_STATE;
                themesFilterValues.map(d => {
                    stt[d.value] = subThemesFilterValues[d.value];
                });

                setSubThemeFilter(stt);
                subThemesFilterValues = stt;

                break;
            case "sub-theme":
                // get the current state
                let management = subThemesFilterValues["How can we optimise our management impact?"];
                let innovation =
                    subThemesFilterValues["How can we improve our management through innovation and technology?"];
                let key = subThemesFilterValues["What is the condition and trend of key values?"];
                let how = subThemesFilterValues["How is the Reef used?"];

                if (filters.length > 0) {
                    // also only turn on my theme if subthemes are selected.
                    if (themesFilterValues.filter(d => d.value === themeForSubThemeFilter.slug).length === 0) {
                        const selectedThemes = [
                            ...themesFilterValues,
                            { value: themeForSubThemeFilter.slug, label: themeForSubThemeFilter.theme },
                        ];
                        setThemeFilter(selectedThemes);
                        themesFilterValues = selectedThemes;
                    }
                } else {
                    // if subthemes are empty
                    // make sure my parent isn't in the theme filter
                    const selectedThemes = themesFilterValues.filter(d => d.value !== themeForSubThemeFilter.slug);
                    setThemeFilter(selectedThemes);
                    themesFilterValues = selectedThemes;
                }

                switch (themeForSubThemeFilter.theme) {
                    case "How can we optimise our management impact?":
                        management = filters;
                        break;
                    case "How can we improve our management through innovation and technology?":
                        innovation = filters;
                        break;
                    case "What is the condition and trend of key values?":
                        key = filters;
                        break;
                    case "How is the Reef used?":
                        how = filters;
                        break;
                    default:
                        console.log(filters, themeForSubThemeFilter);
                }

                // only set the relevant state for the theme
                const st = {
                    "How can we optimise our management impact?": management,
                    "How can we improve our management through innovation and technology?": innovation,
                    "What is the condition and trend of key values?": key,
                    "How is the Reef used?": how,
                };
                setSubThemeFilter(st);
                subThemesFilterValues = st;

                // if there are no sub-themes selected, reset all the themes
                if (subThemesFilterValues.length === 0) {
                    setThemeFilter([]);
                    themesFilterValues = [];
                }

                break;
            case "keyword":
                keywordsFilterValues = filters;
                setKeywordFilter(filters);
                break;
            case "completed":
                hideCompletedFilter = filters;
                break;
            case "search":
                break;
            default:
                console.log("default filter", filters);
        }

        let opps = opportunities.filter(o =>
            topicsFilterValues.length > 0 ? topicsFilterValues.map(d => d.value).includes(o.topic.slug) : true
        );

        opps = opps.filter(o =>
            keywordsFilterValues.length > 0
                ? keywordsFilterValues.map(d => d.value).some(d => o.keywords.includes(d))
                : true
        );
        opps = opps.filter(o =>
            themesFilterValues.length > 0
                ? themesFilterValues.map(d => d.label).some(d => [o.themes.primary, o.themes.secondary].includes(d))
                : true
        );

        // only filter by sub theme if the theme is on
        opps = opps.filter(o => {
            const primarySubTheme =
                subThemesFilterValues[o.themes.primary].length > 0
                    ? subThemesFilterValues[o.themes.primary]
                          .map(d => d.label)
                          .some(d => o.themes.subthemes.map(e => (e ? e.value : null)).includes(d))
                    : true;

            let secondarySubTheme = true;
            if (o.themes.secondary) {
                secondarySubTheme =
                    subThemesFilterValues[o.themes.secondary].length > 0
                        ? subThemesFilterValues[o.themes.secondary]
                              .map(d => d.label)
                              .some(d => o.themes.subthemes.map(e => (e ? e.value : null)).includes(d))
                        : true;
            }
            return primarySubTheme || secondarySubTheme;
        });

        // take into account opportunitySearch
        if (opportunitySearch.length > 0) {
            opps = opps.filter(o => {
                // match any text contained in opportunities
                const oppSearch = [
                    o.long,
                    o.short,
                    o.reef2050,
                    o.search,
                    o.keywords,
                    o.topic.topic,
                    o.themes.primary,
                    o.themes.secondary ? o.themes.secondary : "",
                    o.themes.subthemes[0] ? o.themes.subthemes[0].value : "",
                    o.themes.subthemes[1] ? o.themes.subthemes[1].value : "",
                ].join(" ");

                const re = new RegExp(opportunitySearch, "i");
                return oppSearch.search(re) > -1;
            });
        }

        const tops =
            topicsFilterValues.length > 0
                ? topics.filter(d => topicsFilterValues.map(d => d.value).includes(d.slug))
                : topics;

        const completedOpps = opps.filter(d => d.status === "Completed");
        setCompleted(completedOpps.length);
        opps = hideCompletedFilter ? opps.filter(d => d.status !== "Completed") : opps;

        setFilteredTopics(tops);
        setFilteredOpportunities(opps);
    };

    React.useEffect(() => {
        if (themesData && childrenData) {
            const themes = themesData.map(t => {
                return {
                    ...t,
                    children: childrenData.filter(c => c.parent === t.theme),
                };
            });

            setThemes(themes.sort((a, b) => a.order - b.order));
        }
    }, [themesData, childrenData]);

    React.useEffect(() => {
        if (topicsData) {
            setTopics(topicsData.sort((a, b) => a.topic - b.topic));
        }
    }, [topicsData]);

    React.useEffect(() => {
        if (opportunitiesData && childrenData && topicsData) {
            const opportunities = opportunitiesData.map((d, i) => {
                return {
                    id: i,
                    short: d["Display text"] || d["Research opportunity"],
                    long: d["Display text"] ? d["Research opportunity"] : "",
                    reef2050: d["Reef 2050 objectives"],
                    keywords: [d["Keyword 1"], d["Keyword 2"], d["Keyword 3"]],
                    themes: {
                        primary: d["Primary parent theme"],
                        secondary: d["Secondary parent theme"],
                        subthemes: [
                            childrenData.find(e => e.id === d["Child 1"]),
                            d["Child 2"] ? childrenData.find(e => e.id === d["Child 2"]) : null,
                        ],
                    },
                    search: d["Search keywords"],
                    topic: topicsData.find(e => d["Topic"] === e.topic),
                    status: d["Status"],
                };
            });
            setOpportunities(opportunities);
        }
    }, [opportunitiesData, childrenData, topicsData]);

    React.useEffect(() => {
        if (themes && opportunities && topics) {
            const topicOptions = topics
                .map(d => {
                    return {
                        value: d.slug,
                        label: d.topic,
                        type: "topic",
                    };
                })
                .sort((a, b) => a.label.localeCompare(b.label));

            const keywordOptions = [...new Set(opportunities.map(d => d.keywords).flat())]
                .map(d => {
                    return {
                        value: d,
                        label: d,
                        type: "keyword",
                    };
                })
                .sort((a, b) => a.label.localeCompare(b.label));

            const themeOptions = themes
                .map(d => {
                    return {
                        value: d.slug,
                        label: d.theme,
                        type: "theme",
                    };
                })
                .sort((a, b) => a.label.localeCompare(b.label));

            const subThemeOptions = themes
                .map(d => {
                    return d.children.map(e => {
                        return {
                            value: e.id,
                            label: e.value,
                            type: "sub-theme",
                        };
                    });
                })
                .flat()
                .sort((a, b) => a.label.localeCompare(b.label));

            const opportunityOptions = opportunities.map(d => {
                return {
                    value: [
                        d.long,
                        d.reef2050,
                        d.search,
                        d.keywords,
                        d.topic.topic,
                        d.themes.primary,
                        d.themes.secondary ? d.themes.secondary : "",
                        d.themes.subthemes[0] ? d.themes.subthemes[0].value : "",
                        d.themes.subthemes[1] ? d.themes.subthemes[1].value : "",
                    ].join(" "),
                    label: d.short,
                    type: "opportunity",
                };
            });

            // search input can match topics, themes (and sub-themes), opportunities (short, long, reef2050, search, keywords)
            const searchInput = [
                ...themeOptions,
                ...subThemeOptions,
                ...topicOptions,
                ...keywordOptions,
                ...opportunityOptions,
            ];
            setSearchOptions(searchInput);
            setOpportunitySearch([]);
        }
    }, [themes, opportunities, topics]);

    return (
        <div
            id="root"
            style={
                isMobile
                    ? {}
                    : {
                          backgroundImage: "url(/wave.svg)",
                          backgroundPositionY: "320px",
                          backgroundRepeatY: "no-repeat",
                          backgroundRepeatX: "repeat",
                          backgroundRepeat: "repeat-x",
                      }
            }
        >
            <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
                <Intro />
                {themes && <Themes themes={themes} />}
                {themes && opportunities && topics && searchOptions && (
                    <Search
                        searchOptions={searchOptions}
                        handleFilterAndSearch={handleFilterAndSearch}
                        searchFilterState={searchFilter}
                        searchState={searchState}
                    />
                )}
                {themes && opportunities && topics && searchOptions && (
                    <Explore
                        themes={themes}
                        opportunities={opportunities}
                        topics={topics}
                        searchOptions={searchOptions}
                        filteredOpportunities={filteredOpportunities}
                        filteredTopics={filteredTopics}
                        handleTopicFilter={handleTopicFilter}
                        handleKeywordFilter={handleKeywordFilter}
                        handleThemeFilter={handleThemeFilter}
                        handleSubThemeFilter={handleSubThemeFilter}
                        handleReset={handleReset}
                        handleOpportunitySearch={handleOpportunitySearch}
                        handleOpportunityInput={handleOpportunityInput}
                        handleOpportunitySearchReset={handleOpportunitySearchReset}
                        opportunitySearch={opportunitySearch}
                        topicFilterState={topicFilter}
                        keywordFilterState={keywordFilter}
                        themeFilterState={themeFilter}
                        subThemeFilterState={subThemeFilter}
                        selectedOpportunity={selectedOpportunity}
                        handleToggleView={handleToggleView}
                        view={view}
                        handleHideCompleted={handleHideCompleted}
                        hideCompleted={hideCompleted}
                        completed={completed}
                        handleScrollToTop={handleScrollToTop}
                    />
                )}
            </div>
        </div>
    );
}

export default App;
